import message from "../../message/source/MessageSource";
import CsrfToken from "../model/CsrfToken";
import {makeAutoObservable, runInAction} from "mobx";
import CommonUtil from "utils/CommonUtil";
import StringUtil from "utils/StringUtil";
import LoginModel from "../model/LoginModel";
import UserModel from "../model/UserModel";
import UserSessionModel from "../model/UserSessionModel";
import userRepository from "../repository/UserRepository";
import userRestRepository from "../repository/UserRestRepository";
import UIUtil from "../../../utils/UIUtils";

/**
 * 사용자 Store
 */
export class UserStore {
    constructor() {
        makeAutoObservable(this);
    }

    private _loginModel: LoginModel | undefined;
    private _csrfToken: CsrfToken | undefined;
    private _userModel: UserModel | undefined;
    private _userSessionModel: UserSessionModel | undefined;

    private defalutRemainTime = 0;
    private sessionTimeHandler?: number;

    public sessionCheckClientTime?: number;

    public get loginModel() {
        return this._loginModel;
    }

    public set loginModel(_loginModel: LoginModel | undefined) {
        this._loginModel = _loginModel;
    }

    public get authenticated(): boolean {
        return this.userModel != null && this.userModel.getLoginTy() != null;
    }

    public get csrfToken() {
        return this._csrfToken;
    }

    public set csrfToken(csrfToken: CsrfToken | undefined) {
        this._csrfToken = csrfToken;
    }

    public get userModel() {
        return this._userModel;
    }

    public set userModel(userModel: UserModel | undefined) {
        this._userModel = userModel;
    }

    public get userSessionModel() {
        return this._userSessionModel;
    }

    public set userSessionModel(_userSessionModel: UserSessionModel | undefined) {
        this._userSessionModel = _userSessionModel;
    }

    /**
     * CSRF 토큰 갱신
     */
    public async updateToken() {
        const result = await userRestRepository.readToken();

        if (result == null) {
            return;
        }

        this.csrfToken = new CsrfToken(result);
    }

    /**
     * 사용자 조회
     */
    public async readUser() {
        const result = await userRestRepository.readUser();

        if (result.MODEL && result.TOKEN && result.SESSION) {
            this.loginModel = new LoginModel(result.MODEL.username, result.MODEL.loginTy);
            this.userModel = new UserModel(result.MODEL);
            this.csrfToken = new CsrfToken(result.TOKEN);
            this.userSessionModel = new UserSessionModel(result.SESSION);

            this.defalutRemainTime = this.userSessionModel.remainTime || 0;
            this.setSessionCheckClientTime(new Date().getTime() - 500);
            this.setSessionRemainTime();
            sessionStorage.setItem("user", JSON.stringify(this));
        }
    }

    /**
     * 세션 조회
     *
     * @param reload
     */
    public async resetSession(reload?: boolean) {
        if (!this.authenticated) {
            return;
        }

        if (!this.userSessionModel || !this.userSessionModel.remainTime) {
            return;
        }

        this.setSessionCheckClientTime(new Date().getTime() - 500);

        if (!reload) {
            this.userSessionModel.setRemainTime(this.defalutRemainTime);
            return;
        }

        const result = await userRestRepository.readSession();
        if (result) {
            runInAction(() => {
                this.defalutRemainTime = result.remainTime;
                if (this.userSessionModel) {
                    this.userSessionModel.setRemainTime(result.remainTime);
                    this.userSessionModel.setCheckServerTime(result.checkServerTime);
                    this.userSessionModel.setMaxInactiveInterval(result.maxInactiveInterval);
                    this.userSessionModel.setCreationTime(result.creationTime);
                } else {
                    this.userSessionModel = new UserSessionModel(result);
                }
            })
        }

        this.setSessionRemainTime();
    }

    /**
     * 세션 남은시간 반환
     */
    public get sessionRemainTime() {
        if (this.userSessionModel == null || this.userSessionModel.remainTime == null) {
            return undefined;
        }

        // const remainTime = Math.max(this.userSessionModel.getRemainTime(), 0);
        // const minute = String(Math.floor(remainTime / 60));
        // const second = String(remainTime % 60);
        //
        // if (Number(minute) <= 10) {
        //     UIUtil.alert("10분 후 자동 로그아웃 됩니다");
        // }
        //
        // return minute + ":" + StringUtil.leftPad(second, 2, "0");
        return Math.max(this.userSessionModel.remainTime, -1);
    }

    /**
     * 세션 남은시간 설정
     */
    private setSessionRemainTime() {
        if (!this.userSessionModel) {
            return;
        }

        if (this.sessionCheckClientTime !== undefined) {
            const intervalSecond = Math.floor((new Date().getTime() - this.sessionCheckClientTime) / 1000);
            this.userSessionModel.decrement(intervalSecond);
            window.setTimeout(() => this.setSessionRemainTime(), 500);
        }
    }

    /**
     * 로그인
     *
     * @param form
     */
    public async login(form: HTMLFormElement) {
        if (StringUtil.isEmpty(form.USERNAME.value)) {
            await UIUtil.alert(message.get("common.user.message.inputLoginUsername", "로그인 아이디를 입력하세요."));

            return {result: false, focus: "username"};
        }

        if (StringUtil.isEmpty(form.PASSWORD.value)) {
            await UIUtil.alert(message.get("common.user.message.inputLoginPassword", "로그인 암호를 입력하세요."));

            return {result: false, focus: "password"};
        }

        const result: {
            MODEL?: any,
            TOKEN?: { headerName: string; parameterName: string; token: string },
            SESSION?: any
            CODE?: string;
            MESSAGE?: string;
        } = await userRepository.login(form)
            .then((result) => {
                return {
                    MODEL: result.MODEL,
                    SESSION: result.SESSION,
                    TOKEN: result.TOKEN
                };
            })
            .catch((error: { COMMON_HEAD: Error }) => {
                const errorCode = CommonUtil.jexjs.getJexErrorCode(error);
                const errorMessage = CommonUtil.jexjs.getJexErrorMessage(error);

                return {
                    CODE: errorCode,
                    MESSAGE: errorMessage
                };
            });


        if (result && result.MODEL) {
            this.loginModel = new LoginModel(result.MODEL.username, result.MODEL.loginTy);
            this.loginModel?.saveCookies();

            this.csrfToken = new CsrfToken(result.TOKEN);
            this.userModel = new UserModel(result.MODEL);
            this.userSessionModel = new UserSessionModel(result.SESSION);
            this.defalutRemainTime = this.userSessionModel?.remainTime || 0;

            this.setSessionCheckClientTime(new Date().getTime() - 500);
            this.setSessionRemainTime();

            sessionStorage.setItem("user", JSON.stringify(this));

            return {result: true};
        } else {
            return {result: false, code: result.CODE, message: result.MESSAGE};
        }
    }

    /**
     * 로그아웃
     */
    public logout(isAdmin: boolean) {
        UIUtil.confirm(message.get("common.user.message.logout.confirm", "로그아웃 하시겠습니까?")).then(r1 => {
            if (r1) {
                userRepository.logout().then(() => {
                    this.clearUserStore(isAdmin);
                });
            }
        })
    }

    /**
     * 로그아웃 quietly
     */
    public async logoutQuietly(isAdmin: boolean) {
        await userRepository.logout();
        this.clearUserStore(isAdmin);
    }

    public clearUserStore(isAdmin: boolean) {
        this.clearUserStoreNoReplace();
    }

    public clearUserStoreNoReplace() {
        runInAction(() => {
            this.loginModel = undefined;
            this.userModel = undefined;
            this.userSessionModel = undefined;
            window.clearInterval(this.sessionTimeHandler);
            this.sessionTimeHandler = undefined;
            this.setSessionCheckClientTime(undefined);
            sessionStorage.removeItem("login");
            sessionStorage.removeItem("user");
        })
    }

    public setSessionCheckClientTime(time:number|undefined) {
        this.sessionCheckClientTime = time;
    }

    public canAdminAccess() {
        const canAccessRoles = [
            'ROLE_MONEY_USER',
            'ROLE_ADMIN_USER',
            'ROLE_DEPT_HEAD',
            'ROLE_GENERAL_MANG',
            'ROLE_ADMIN',
            'ROLE_SUPER'
        ];
        return canAccessRoles.some(auth => userStore.userModel?.getAuthorities()?.includes(auth));

    }

    public getLoginUrl(isAdmin:boolean) {
        // 2024-08-08 현업 및 PM(김평식) 요청으로 Admin 로그인 페이지 로 이동 안함.
        // return isAdmin ? `/admin/adm_login/admlogin` : `/main/usr_1000/usr_1300`;
        return `/main/usr_1000/usr_1300`;
    }
}

const userStore = new UserStore();

export default userStore;
